import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Route, Clock, Calendar, RefreshCw,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  MapPin, ArrowRight, AlertTriangle, X, Map,
  Car, CheckCircle, AlertCircle, Activity,
  Settings, List,
} from 'lucide-react';
import lprJourneyAPI from '../../../../services/lprJourneyAPI';
import imageService from '../../../../services/imageHandlerAPI';
import LprJourneyGhostPreview from './LprJourneyGhostPreview';
import lprJourneyVehicleRouteAnalysisImg from '../../../../images/lprJourneyVehicleRouteAnalysis.png';
import {
  calcDelta,
  calcAnomalyScore,
  HISTORY_WINDOW_DAYS,
  TREND_NEUTRAL_TOLERANCE_PCT,
} from './CameraTransitions';
import './VehicleRouteAnalysis.css';

/* ─── re-export anomaly colour logic (mirrors CameraTransitions exactly) ─── */

/**
 * Colour-encode slowness using delta_pct vs history baseline.
 *   green  = normal  (delta ≤ 15 %)
 *   amber  = elevated (15 < delta ≤ 40 %)
 *   red    = slow    (delta > 40 %)
 *   indigo = no history available
 */
const routeColor = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) return '#6366f1'; // no history
  if (deltaPct > 40)  return '#ef4444'; // red   – slow
  if (deltaPct > 15)  return '#f59e0b'; // amber – elevated
  return '#22c55e';                      // green – normal
};

const routeColorClass = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) return 'vra-leg--nohistory';
  if (deltaPct > 40)  return 'vra-leg--slow';
  if (deltaPct > 15)  return 'vra-leg--elevated';
  return 'vra-leg--normal';
};

const routeColorLabel = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) return 'No baseline';
  if (deltaPct > 40)  return 'Slow';
  if (deltaPct > 15)  return 'Elevated';
  return 'Normal';
};

/* ─── helpers ────────────────────────────────────────────────────────────── */

/**
 * Return today's date string (YYYY-MM-DD) in the given IANA timezone.
 * Falls back to the system local date when timezone is null/undefined.
 */
const todayInTz = (timezone) => {
  if (!timezone) {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const fmtDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

const fmtDay = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

/**
 * Format a local datetime string (YYYY-MM-DDTHH:mm:ss, no Z) as time only.
 * The backend returns datetimes already converted to the network's local timezone,
 * so we parse them as local time by replacing 'T' with a space.
 */
const fmtTime = (localStr) => {
  if (!localStr) return '—';
  const d = new Date(localStr.replace('T', ' '));
  if (isNaN(d)) return localStr;
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

/**
 * Format a local datetime string (YYYY-MM-DDTHH:mm:ss, no Z) as date + time.
 */
const fmtDateTime = (localStr) => {
  if (!localStr) return '—';
  const d = new Date(localStr.replace('T', ' '));
  if (isNaN(d)) return localStr;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

/**
 * Given an ordered list of detections, compute the leg durations.
 * Leg[i] = time from last_seen[i] to detected_at[i+1]  (matches Camera Transitions logic).
 * Returns an array of { fromDetection, toDetection, legSeconds } objects.
 */
/**
 * Parse a local datetime string (YYYY-MM-DDTHH:mm:ss, no Z) to a timestamp.
 * Replacing 'T' with a space ensures browsers parse it as local time rather
 * than UTC (ISO 8601 strings with 'T' but no offset are treated as UTC in
 * some environments).
 */
const parseLocalTs = (str) => str ? new Date(str.replace('T', ' ')).getTime() : NaN;

const buildLegs = (detections) => {
  const legs = [];
  for (let i = 0; i < detections.length - 1; i++) {
    const from = detections[i];
    const to   = detections[i + 1];
    const fromTs = from.last_seen ? parseLocalTs(from.last_seen)
                                  : parseLocalTs(from.detected_at);
    const toTs   = parseLocalTs(to.detected_at);
    const legSeconds = (toTs - fromTs) / 1000;
    legs.push({ fromDetection: from, toDetection: to, legSeconds: legSeconds > 0 ? legSeconds : null });
  }
  return legs;
};

/**
 * Compute expected journey duration = sum of historical avg for each leg.
 * Returns null if any leg has no history (we can still show partial).
 */
const calcExpectedDuration = (legs, historyMap) => {
  let total = 0;
  let hasAll = true;
  for (const leg of legs) {
    const key = `${leg.fromDetection.camera_id}|${leg.toDetection.camera_id}`;
    const hist = historyMap[key]?.avg ?? null;
    if (hist === null) { hasAll = false; continue; }
    total += hist;
  }
  return hasAll ? total : (total > 0 ? total : null);
};

/* ─── sub-components ─────────────────────────────────────────────────────── */

/** Mirrors CameraTransitions StatCard exactly. */
const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="ct-stat-card">
    <div className="ct-stat-icon"><Icon size={16} /></div>
    <div className="ct-stat-body">
      <p className="ct-stat-value">{value}</p>
      <p className="ct-stat-label">{label}</p>
      {sub && <p className="ct-stat-sub">{sub}</p>}
    </div>
  </div>
);

/** Mirrors CameraTransitions HistorySkeleton exactly. */
const HistorySkeleton = () => (
  <span className="ct-history-skeleton" aria-label="Loading historical average" />
);

/** Mirrors CameraTransitions AnomalyBadge exactly. */
const AnomalyBadge = ({ label = '!' }) => (
  <span className="ct-anomaly-badge" title="Anomaly detected">
    {label}
  </span>
);

/** Status pill for journey status (active / completed / incomplete). */
const StatusPill = ({ status }) => {
  const cls = {
    completed:  'vra-status--completed',
    active:     'vra-status--active',
    incomplete: 'vra-status--incomplete',
  }[status] || 'vra-status--incomplete';

  const icon = {
    completed:  <CheckCircle size={10} />,
    active:     <Activity size={10} />,
    incomplete: <AlertCircle size={10} />,
  }[status] || null;

  return (
    <span className={`vra-status-pill ${cls}`}>
      {icon}
      {status}
    </span>
  );
};

/* ─── Journey Map Popup ──────────────────────────────────────────────────── */

const JMAP_W = 1000;
const JMAP_H = 700;
const JMAP_NODE_R = 18;

/**
 * JourneyMapPopup
 *
 * Full-screen modal showing the animated route of a single journey on the
 * background map. Camera pins are drawn in detected_at order with a directed
 * animated path between them. Slow legs are highlighted in anomaly colour.
 *
 * Props:
 *   journey      {object}   – journey row
 *   detections   {Array}    – ordered detections for this journey
 *   legs         {Array}    – pre-computed legs (from buildLegs)
 *   historyMap   {object}   – "fromId|toId" → { avg, min, max }
 *   config       {object}   – { cameras, hasBackgroundImage }
 *   networkId    {string}
 *   onClose      {function}
 */
const JourneyMapPopup = ({ journey, detections, legs, historyMap, config, networkId, onClose }) => {
  const [bgUrl, setBgUrl]               = useState(null);
  const [bgLoading, setBgLoading]       = useState(false);
  const [bgImageReady, setBgImageReady] = useState(false);
  const [animStep, setAnimStep]         = useState(0);  // which leg is currently "lit"
  const [hoveredLeg, setHoveredLeg]     = useState(null); // "fromId|toId"
  const [tooltip, setTooltip]           = useState(null); // { x, y, leg, hist }
  const svgRef  = useRef(null);
  const animRef = useRef(null);

  /* ── load background image ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!config?.hasBackgroundImage || !networkId) return;
    setBgLoading(true);
    setBgImageReady(false);
    imageService.getImageUrl(`lpr-journey/${networkId}`, 7200)
      .then(res => { if (res.success) setBgUrl(res.url); })
      .catch(() => {})
      .finally(() => setBgLoading(false));
  }, [networkId, config?.hasBackgroundImage]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Whether the map content (pins + routes) should be visible */
  const mapContentReady = !config?.hasBackgroundImage || bgImageReady;

  /* ── ESC closes popup ──────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* ── lock body scroll ──────────────────────────────────────────────────── */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  /* ── animate path step-by-step ─────────────────────────────────────────── */
  useEffect(() => {
    if (legs.length === 0) return;
    let step = 0;
    setAnimStep(0);
    animRef.current = setInterval(() => {
      step = (step + 1) % (legs.length + 1);
      setAnimStep(step);
    }, 900);
    return () => clearInterval(animRef.current);
  }, [legs.length]);

  /* ── build node positions from config ─────────────────────────────────── */
  const configCams = config?.cameras || [];
  const configMap  = useMemo(() => {
    const m = {};
    configCams.forEach(c => { m[c.camera_id] = c; });
    return m;
  }, [configCams]);

  const hasPositions = detections.some(d => {
    const c = configMap[d.camera_id];
    return c?.pos_x != null && c?.pos_y != null;
  });

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div
      className="ct-trm-modal-overlay vra-map-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Journey Route Map"
    >
      <div className="ct-trm-modal vra-map-modal">
        {/* Header */}
        <div className="ct-trm-modal-header">
          <span className="ct-trm-title">
            <Map size={14} className="ct-trm-title-icon" />
            Journey Route — {journey.plate}
            <StatusPill status={journey.status} />
          </span>
          <div className="ct-trm-modal-header-right">
            <span className="ct-trm-subtitle">
              {fmtDateTime(journey.journey_start)} · {detections.length} camera{detections.length !== 1 ? 's' : ''} · ESC to close
            </span>
            <button
              className="ct-trm-modal-close-btn"
              onClick={onClose}
              title="Close map"
              aria-label="Close map"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="ct-trm-legend">
          <span className="ct-trm-legend-title">Leg time vs baseline:</span>
          <span className="ct-trm-legend-item">
            <span className="ct-trm-legend-swatch" style={{ background: '#22c55e' }} />
            Normal (≤+15%)
          </span>
          <span className="ct-trm-legend-item">
            <span className="ct-trm-legend-swatch" style={{ background: '#f59e0b' }} />
            Elevated (+15–40%)
          </span>
          <span className="ct-trm-legend-item">
            <span className="ct-trm-legend-swatch" style={{ background: '#ef4444' }} />
            Slow (&gt;+40%)
          </span>
          <span className="ct-trm-legend-item">
            <span className="ct-trm-legend-swatch" style={{ background: '#6366f1' }} />
            No baseline
          </span>
        </div>

        {/* Map canvas */}
        <div className="ct-trm-modal-canvas">
            {!hasPositions ? (
            <div className="ct-trm-empty-state">
              <MapPin size={20} className="ct-trm-empty-icon" />
              <p className="ct-trm-empty-title">Camera positions not configured</p>
              <p className="ct-trm-empty-sub">
                Use <strong>Configure Map</strong> above to place your cameras. Once configured,
                you can search by licence plate to see each vehicle&apos;s full journey on the map.
              </p>
            </div>
          ) : (
            <>
              {/* Loading spinner — shown while background image is being fetched / decoded */}
              {(bgLoading || (bgUrl && !bgImageReady)) && (
                <div className="ct-map-loading-overlay">
                  <div className="ct-spinner ct-map-spinner" />
                  <span className="ct-map-loading-text">Loading map…</span>
                </div>
              )}

              <svg
                ref={svgRef}
                viewBox={`0 0 ${JMAP_W} ${JMAP_H}`}
                width="100%"
                height="100%"
                className="ct-trm-svg"
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  {/* Arrowhead markers per colour */}
                  {['#22c55e', '#f59e0b', '#ef4444', '#6366f1', '#ffffff'].map(color => {
                    const id = `jmap-arrow-${color.replace('#', '')}`;
                    return (
                      <marker key={id} id={id} markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
                        <path d="M0,0 L0,7 L7,3.5 z" fill={color} />
                      </marker>
                    );
                  })}
                  {/* Animated dash marker */}
                  <marker id="jmap-arrow-anim" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
                    <path d="M0,0 L0,7 L7,3.5 z" fill="white" />
                  </marker>
                  {/* Glow filter */}
                  <filter id="jmap-glow" x="-60%" y="-60%" width="220%" height="220%">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Background fill */}
                <rect x="0" y="0" width={JMAP_W} height={JMAP_H} fill="rgba(8,12,24,0.92)" />

                {/* Grid (no bg image) */}
                {!bgUrl && Array.from({ length: 8 }, (_, i) => (
                  <line key={`vg${i}`}
                    x1={((i + 1) * JMAP_W) / 9} y1="0"
                    x2={((i + 1) * JMAP_W) / 9} y2={JMAP_H}
                    stroke="rgba(255,255,255,0.03)" strokeWidth="1"
                  />
                ))}
                {!bgUrl && Array.from({ length: 5 }, (_, i) => (
                  <line key={`hg${i}`}
                    x1="0" y1={((i + 1) * JMAP_H) / 6}
                    x2={JMAP_W} y2={((i + 1) * JMAP_H) / 6}
                    stroke="rgba(255,255,255,0.03)" strokeWidth="1"
                  />
                ))}

                {/* Background image — onLoad fires once the browser has decoded it */}
                {bgUrl && (
                  <image
                    href={bgUrl}
                    x="0" y="0"
                    width={JMAP_W} height={JMAP_H}
                    opacity="0.4"
                    preserveAspectRatio="xMidYMid meet"
                    onLoad={() => setBgImageReady(true)}
                  />
                )}

                {/* ── Route legs (deferred until background image is ready) ── */}
                {mapContentReady && legs.map((leg, i) => {
                const fromCam = configMap[leg.fromDetection.camera_id];
                const toCam   = configMap[leg.toDetection.camera_id];
                if (!fromCam?.pos_x || !toCam?.pos_x) return null;

                const x1 = fromCam.pos_x * JMAP_W;
                const y1 = fromCam.pos_y * JMAP_H;
                const x2 = toCam.pos_x   * JMAP_W;
                const y2 = toCam.pos_y   * JMAP_H;

                const dx   = x2 - x1;
                const dy   = y2 - y1;
                const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                const ux   = dx / dist;
                const uy   = dy / dist;

                const sx = x1 + ux * (JMAP_NODE_R + 5);
                const sy = y1 + uy * (JMAP_NODE_R + 5);
                const ex = x2 - ux * (JMAP_NODE_R + 10);
                const ey = y2 - uy * (JMAP_NODE_R + 10);

                const key      = `${leg.fromDetection.camera_id}|${leg.toDetection.camera_id}`;
                const histEntry = historyMap[key] ?? null;
                const hist     = histEntry?.avg ?? null;
                const histMin  = histEntry?.min ?? null;
                const histMax  = histEntry?.max ?? null;
                const delta    = calcDelta(leg.legSeconds, hist);
                const color    = routeColor(delta);
                const arrowId  = `jmap-arrow-${color.replace('#', '')}`;
                const isAnim   = animStep === i + 1;
                const isHovered = hoveredLeg === key;

                const handleMouseMove = (e) => {
                  const svg = svgRef.current;
                  if (!svg) return;
                  const rect = svg.getBoundingClientRect();
                  const scaleX = JMAP_W / rect.width;
                  const scaleY = JMAP_H / rect.height;
                  setTooltip({
                    x: (e.clientX - rect.left) * scaleX,
                    y: (e.clientY - rect.top)  * scaleY,
                    leg, hist, histMin, histMax, delta, color,
                  });
                };

                return (
                  <g key={`leg-${i}`}>
                    {/* Wide invisible hit area for hover */}
                    <line
                      x1={sx} y1={sy} x2={ex} y2={ey}
                      stroke="transparent"
                      strokeWidth="18"
                      strokeLinecap="round"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredLeg(key)}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={() => { setHoveredLeg(null); setTooltip(null); }}
                    />
                    {/* Glow halo for animated step or hover */}
                    {(isAnim || isHovered) && (
                      <line
                        x1={sx} y1={sy} x2={ex} y2={ey}
                        stroke={isHovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.2)'}
                        strokeWidth={isHovered ? 12 : 10}
                        strokeLinecap="round"
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    {/* Main leg line */}
                    <line
                      x1={sx} y1={sy} x2={ex} y2={ey}
                      stroke={isAnim || isHovered ? '#ffffff' : color}
                      strokeWidth={isAnim || isHovered ? 4 : 2.5}
                      strokeLinecap="round"
                      opacity={isAnim || isHovered ? 1 : (hoveredLeg ? 0.45 : 0.75)}
                      markerEnd={`url(#${isAnim || isHovered ? 'jmap-arrow-anim' : arrowId})`}
                      style={{ pointerEvents: 'none', transition: 'stroke 0.15s, opacity 0.15s' }}
                    />
                  </g>
                );
              })}

              {/* ── SVG Tooltip ─────────────────────────────────────────────── */}
              {mapContentReady && tooltip && (() => {
                const { leg, hist, histMin, histMax, delta, color } = tooltip;
                const sign     = delta !== null && delta > 0 ? '+' : '';
                const deltaStr = delta !== null
                  ? `${sign}${delta.toFixed(1)}% vs baseline`
                  : 'No baseline';

                const rows = [
                  ['Leg time',        fmtDuration(leg.legSeconds)],
                  ['Baseline avg',    hist !== null ? fmtDuration(hist) : '—'],
                  ['Baseline min/max', histMin !== null
                    ? `${fmtDuration(histMin)} / ${fmtDuration(histMax)}`
                    : '—'],
                  ['vs Baseline',     deltaStr],
                ];

                const TW        = 250;
                const fzHeader  = 11;
                const fzLabel   = 10;
                const fzValue   = 10;
                const rowStride = 19;
                const firstRowY = 44;
                const headerY   = 18;
                const dividerY  = 27;
                const TH        = firstRowY + rows.length * rowStride + 10;

                const tx = Math.min(tooltip.x + 14, JMAP_W - TW - 8);
                const ty = Math.min(tooltip.y - 10, JMAP_H - TH - 8);

                const fromName = leg.fromDetection.camera_name;
                const toName   = leg.toDetection.camera_name;
                const headerText = `${fromName.length > 13 ? fromName.slice(0, 12) + '…' : fromName} → ${toName.length > 13 ? toName.slice(0, 12) + '…' : toName}`;

                return (
                  <g style={{ pointerEvents: 'none' }}>
                    {/* Shadow */}
                    <rect x={tx + 2} y={ty + 2} width={TW} height={TH} rx="8" fill="rgba(0,0,0,0.45)" />
                    {/* Background */}
                    <rect x={tx} y={ty} width={TW} height={TH} rx="8"
                      fill="#1e293b" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                    {/* Colour accent bar */}
                    <rect x={tx} y={ty} width={4} height={TH} rx="2" fill={color} />
                    {/* From → To header */}
                    <text x={tx + 14} y={ty + headerY} fill="#e2e8f0" fontSize={fzHeader} fontWeight="700">
                      {headerText}
                    </text>
                    {/* Divider */}
                    <line x1={tx + 14} y1={ty + dividerY} x2={tx + TW - 14} y2={ty + dividerY}
                      stroke="rgba(255,255,255,0.09)" strokeWidth="1" />
                    {/* Stats rows */}
                    {rows.map(([label, val], idx) => {
                      const rowY = ty + firstRowY + idx * rowStride;
                      return (
                        <g key={label}>
                          <text x={tx + 14} y={rowY}
                            fill="#94a3b8" fontSize={fzLabel} fontWeight="500">{label}</text>
                          <text x={tx + TW - 14} y={rowY}
                            fill="#f1f5f9" fontSize={fzValue} fontWeight="700" textAnchor="end">{val}</text>
                        </g>
                      );
                    })}
                  </g>
                );
              })()}

              {/* ── Camera nodes (deferred until background image is ready) ── */}
              {mapContentReady && detections.map((det, i) => {
                const cam = configMap[det.camera_id];
                if (!cam?.pos_x) return null;

                const cx = cam.pos_x * JMAP_W;
                const cy = cam.pos_y * JMAP_H;
                const isFirst = i === 0;
                const isLast  = i === detections.length - 1;

                const fill   = isFirst ? '#16a34a' : isLast ? '#b45309' : '#4f46e5';
                const ring   = isFirst ? '#4ade80' : isLast ? '#fbbf24' : null;
                const nameColor = isFirst ? '#4ade80' : isLast ? '#fbbf24' : 'rgba(255,255,255,0.85)';
                const icon   = isFirst ? '▶' : isLast ? '◀' : '📷';
                const label  = isFirst ? 'START' : isLast ? 'END' : null;

                return (
                  <g key={`node-${det.camera_id}-${i}`}>
                    {/* Glow halo for entry/exit */}
                    {ring && (
                      <>
                        <circle cx={cx} cy={cy} r={JMAP_NODE_R + 7}
                          fill={isFirst ? 'rgba(34,197,94,0.25)' : 'rgba(217,119,6,0.25)'}
                          style={{ pointerEvents: 'none' }}
                        />
                        <circle cx={cx} cy={cy} r={JMAP_NODE_R + 5}
                          fill="none" stroke={ring} strokeWidth="1.5" strokeDasharray="4 3"
                          style={{ pointerEvents: 'none' }}
                        />
                      </>
                    )}
                    {/* Drop shadow */}
                    <circle cx={cx + 2} cy={cy + 2} r={JMAP_NODE_R} fill="rgba(0,0,0,0.35)"
                      style={{ pointerEvents: 'none' }} />
                    {/* Main circle */}
                    <circle cx={cx} cy={cy} r={JMAP_NODE_R}
                      fill={fill} stroke="white" strokeWidth="2"
                      style={{ pointerEvents: 'none' }}
                    />
                    {/* Icon */}
                    <text x={cx} y={cy - (label ? 5 : 0)}
                      fill="white" fontSize={label ? '11' : '14'}
                      textAnchor="middle" dominantBaseline="central"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {icon}
                    </text>
                    {/* START / END label */}
                    {label && (
                      <text x={cx} y={cy + 10}
                        fill="white" fontSize="7" fontWeight="800" letterSpacing="0.5"
                        textAnchor="middle" dominantBaseline="central"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {label}
                      </text>
                    )}
                    {/* Step number badge */}
                    <text
                      x={cx + JMAP_NODE_R - 2} y={cy - JMAP_NODE_R + 2}
                      fill="#e2e8f0" fontSize="8" fontWeight="700"
                      textAnchor="middle" dominantBaseline="central"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {i + 1}
                    </text>
                    {/* Camera name */}
                    <text
                      x={cx} y={cy + JMAP_NODE_R + (ring ? 14 : 12)}
                      fill={nameColor} fontSize="9" fontWeight="600"
                      textAnchor="middle" dominantBaseline="central"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {det.camera_name.length > 14 ? det.camera_name.slice(0, 13) + '…' : det.camera_name}
                    </text>
                    {/* Detected-at timestamp */}
                    <text
                      x={cx} y={cy + JMAP_NODE_R + (ring ? 26 : 24)}
                      fill="#64748b" fontSize="8" fontWeight="400"
                      textAnchor="middle" dominantBaseline="central"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {fmtTime(det.detected_at)}
                    </text>
                  </g>
                );
              })}

              {/* Border */}
              <rect x="0.5" y="0.5" width={JMAP_W - 1} height={JMAP_H - 1}
                fill="none" stroke="rgba(99,102,241,0.15)" strokeWidth="1" rx="12" />
            </svg>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── JourneyRow ─────────────────────────────────────────────────────────── */

/**
 * Renders a single journey's expanded detail: route chain, leg pills,
 * timing stats, and "View on Map" button.
 *
 * visitIndex  {number}  – 1-based visit counter for this plate on this day.
 *                         Pass 0 (or omit) to suppress the label.
 * visitTotal  {number}  – total visits for this plate on this day.
 */
const JourneyRow = ({ journey, historyMap, historyLoading, config, networkId, visitIndex = 0, visitTotal = 1, benchmarkSeconds }) => {
  const [isExpanded, setIsExpanded]       = useState(false);
  const [detections, setDetections]       = useState(null);  // null = not loaded
  const [detLoading, setDetLoading]       = useState(false);
  const [detError, setDetError]           = useState(null);
  const [showMap, setShowMap]             = useState(false);

  /* ── load detections on first expand ──────────────────────────────────── */
  const loadDetections = useCallback(async () => {
    if (detections !== null) return; // already loaded
    setDetLoading(true);
    setDetError(null);
    try {
      const data = await lprJourneyAPI.getJourneyDetections(journey.id);
      // Remove consecutive detections on the same camera to prevent self-pointing legs.
      const deduped = data.filter((d, i) => i === 0 || d.camera_id !== data[i - 1].camera_id);
      setDetections(deduped);
    } catch (err) {
      setDetError(err.message || 'Failed to load detections');
    } finally {
      setDetLoading(false);
    }
  }, [journey.id, detections]);

  const handleToggle = () => {
    if (!isExpanded) loadDetections();
    setIsExpanded(v => !v);
  };

  /* ── derived data ──────────────────────────────────────────────────────── */
  const legs = useMemo(() => {
    if (!detections) return [];
    return buildLegs(detections);
  }, [detections]);

  const expectedDuration = useMemo(() => {
    if (!detections || legs.length === 0) return null;
    return calcExpectedDuration(legs, historyMap);
  }, [detections, legs, historyMap]);

  const delta = useMemo(() => {
    return calcDelta(journey.rtt_seconds, benchmarkSeconds ?? expectedDuration);
  }, [journey.rtt_seconds, benchmarkSeconds, expectedDuration]);

  const anomalyScore = calcAnomalyScore(delta);
  const isAnomalous  = anomalyScore !== Infinity && delta > TREND_NEUTRAL_TOLERANCE_PCT;

  /* ── delta vs benchmark display string ────────────────────────────────── */
  const deltaDisplay = useMemo(() => {
    const ref = benchmarkSeconds ?? expectedDuration;
    if (ref === null || ref === undefined) return null;
    if (journey.rtt_seconds === null) return null;
    const diff = journey.rtt_seconds - ref;
    const sign = diff >= 0 ? '+' : '-';
    const pct  = ref > 0 ? ` (${sign}${Math.abs((diff / ref) * 100).toFixed(1)}%)` : '';
    return `${sign}${fmtDuration(Math.abs(diff))}${pct}`;
  }, [journey.rtt_seconds, benchmarkSeconds, expectedDuration]);

  return (
    <>
      {/* ── Summary row ─────────────────────────────────────────────────── */}
      <div
        className={`vra-journey-row ${isExpanded ? 'vra-journey-row--expanded' : ''}`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleToggle(); }}
        aria-expanded={isExpanded}
      >
        <div className="vra-journey-row-left">
          <span className="vra-journey-id">#{journey.id.slice(-8)}</span>
          {journey.status !== 'completed' && <StatusPill status={journey.status} />}
          {visitIndex > 0 && (
            <span
              className={`vra-visit-badge${visitTotal > 1 ? ' vra-visit-badge--repeat' : ''}`}
              title={`Visit ${visitIndex} of ${visitTotal} on this day`}
            >
              Visit {visitIndex}
            </span>
          )}
          {isAnomalous && !historyLoading && (
            <span className="ct-anomaly-badge vra-journey-anomaly-badge" title="Journey exceeded expected duration">
              <AlertTriangle size={9} />
            </span>
          )}
        </div>

        <div className="vra-journey-row-meta">
          <span className="vra-journey-meta-item">
            <Clock size={11} />
            {fmtTime(journey.journey_start)}
            {journey.journey_end && <> → {fmtTime(journey.journey_end)}</>}
          </span>
          <span className="vra-journey-meta-item">
            <Activity size={11} />
            {journey.detection_count} camera{journey.detection_count !== 1 ? 's' : ''}
          </span>
          <span className="vra-journey-meta-item vra-journey-rtt">
            {fmtDuration(journey.rtt_seconds)}
            {!historyLoading && expectedDuration !== null && (
              <span
                className={`vra-delta-badge ${isAnomalous ? 'vra-delta-badge--over' : 'vra-delta-badge--ok'}`}
                title={`Expected: ${fmtDuration(expectedDuration)}`}
              >
                {deltaDisplay}
              </span>
            )}
            {historyLoading && <HistorySkeleton />}
          </span>
        </div>

        <div className="vra-journey-row-chevron">
          {isExpanded ? <ChevronUp size={13} className="ct-chevron" /> : <ChevronDown size={13} className="ct-chevron" />}
        </div>
      </div>

      {/* ── Expanded detail ─────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="ct-detail-panel vra-journey-detail">
          {detLoading && (
            <div className="ct-loading vra-det-loading">
              <div className="ct-spinner" />
              <span>Loading route…</span>
            </div>
          )}

          {detError && (
            <div className="ct-error">
              <span>⚠️ {detError}</span>
              <button className="ct-retry-btn" onClick={() => { setDetections(null); loadDetections(); }}>
                Retry
              </button>
            </div>
          )}

          {detections && !detLoading && (
            <div className="vra-journey-detail-body">
              {/* ── Stats grid ─────────────────────────────────────────── */}
              <div className="vra-journey-stats-grid">
                <div className="ct-detail-item">
                  <span className="ct-detail-label">Journey ID</span>
                  <span className="ct-detail-value ct-mono">{journey.id}</span>
                </div>
                <div className="ct-detail-item">
                  <span className="ct-detail-label">Status</span>
                  <span className="ct-detail-value"><StatusPill status={journey.status} /></span>
                </div>
                <div className="ct-detail-item">
                  <span className="ct-detail-label">Start time</span>
                  <span className="ct-detail-value">{fmtDateTime(journey.journey_start)}</span>
                </div>
                <div className="ct-detail-item">
                  <span className="ct-detail-label">End time</span>
                  <span className="ct-detail-value">{journey.journey_end ? fmtDateTime(journey.journey_end) : <em className="vra-in-progress">In progress</em>}</span>
                </div>
                <div className="ct-detail-item">
                  <span className="ct-detail-label">Total time (RTT)</span>
                  <span className="ct-detail-value">{fmtDuration(journey.rtt_seconds)}</span>
                </div>
                <div className="ct-detail-item">
                  <span className="ct-detail-label">Delta vs benchmark</span>
                  <span className="ct-detail-value">
                    {historyLoading ? <HistorySkeleton /> : (
                      deltaDisplay
                        ? <span className={isAnomalous ? 'vra-delta-text--over' : 'vra-delta-text--ok'}>{deltaDisplay}</span>
                        : <span className="ct-no-history">Within normal range</span>
                    )}
                  </span>
                </div>
                <div className="ct-detail-item">
                  <span className="ct-detail-label">Cameras detected</span>
                  <span className="ct-detail-value">{journey.detection_count}</span>
                </div>
              </div>

              {/* ── Route chain ────────────────────────────────────────── */}
              {detections.length > 0 && (
                <div className="vra-route-chain-section">
                  <p className="ct-detail-label vra-route-chain-title">
                    <Route size={11} />
                    Route Chain
                  </p>
                  <div className="vra-route-chain">
                    {detections.map((det, i) => {
                      const leg = legs[i - 1]; // leg that arrived at this node
                      const legKey = leg
                        ? `${leg.fromDetection.camera_id}|${leg.toDetection.camera_id}`
                        : null;
                      const legHist  = legKey ? (historyMap[legKey]?.avg ?? null) : null;
                      const legDelta = leg ? calcDelta(leg.legSeconds, legHist) : null;
                      const legClass = leg ? routeColorClass(legDelta) : '';

                      return (
                        <div key={`chain-${i}`} className="vra-chain-item">
                          {/* Leg connector (between nodes) */}
                          {i > 0 && (
                            <div
                              className={`vra-chain-connector ${legClass}`}
                              title={
                                leg
                                  ? `Leg time: ${fmtDuration(leg.legSeconds)} · Baseline: ${fmtDuration(legHist)} · ${routeColorLabel(legDelta)}`
                                  : ''
                              }
                            >
                              <ArrowRight size={11} />
                              <span className="vra-chain-leg-time">{fmtDuration(leg?.legSeconds)}</span>
                            </div>
                          )}
                          {/* Camera node pill */}
                          <div
                            className={`vra-chain-node ${i === 0 ? 'vra-chain-node--first' : i === detections.length - 1 ? 'vra-chain-node--last' : ''}`}
                            title={`Detected: ${fmtDateTime(det.detected_at)}\nLast seen: ${fmtDateTime(det.last_seen)}`}
                          >
                            <span className="vra-chain-node-num">{i + 1}</span>
                            <span className="vra-chain-node-name">{det.camera_name}</span>
                            <span className="vra-chain-node-time">{fmtTime(det.detected_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Leg detail table */}
                  {legs.length > 0 && (
                    <div className="vra-legs-table">
                      <div className="vra-legs-table-header">
                        <span>From</span>
                        <span></span>
                        <span>To</span>
                        <span className="vra-legs-col-right">Leg Time</span>
                        <span className="vra-legs-col-right">Baseline Avg</span>
                        <span className="vra-legs-col-right">Delta</span>
                      </div>
                      {legs.map((leg, i) => {
                        const key      = `${leg.fromDetection.camera_id}|${leg.toDetection.camera_id}`;
                        const hist     = historyMap[key]?.avg ?? null;
                        const delta    = calcDelta(leg.legSeconds, hist);
                        const color    = routeColor(delta);
                        // Delta: show actual time difference + percentage using ± sign
                        const deltaStr = (() => {
                          if (delta === null || hist === null || leg.legSeconds === null) return '—';
                          const diff = leg.legSeconds - hist;
                          const sign = diff >= 0 ? '+' : '-';
                          return `${sign}${fmtDuration(Math.abs(diff))} (${sign}${Math.abs(delta).toFixed(1)}%)`;
                        })();

                        return (
                          <div key={`leg-row-${i}`} className={`vra-legs-row ${routeColorClass(delta)}`}>
                            <span className="vra-legs-cam">{leg.fromDetection.camera_name}</span>
                            <span className="vra-legs-arrow"><ArrowRight size={10} /></span>
                            <span className="vra-legs-cam">{leg.toDetection.camera_name}</span>
                            <span className="vra-legs-col-right vra-legs-time">{fmtDuration(leg.legSeconds)}</span>
                            <span className="vra-legs-col-right vra-legs-hist">
                              {historyLoading ? <HistorySkeleton /> : fmtDuration(hist)}
                            </span>
                            <span className="vra-legs-col-right vra-legs-delta" style={{ color }}>
                              {historyLoading ? <HistorySkeleton /> : deltaStr}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {detections.length === 0 && (
                <div className="ct-empty" style={{ padding: '20px' }}>
                  <p className="ct-empty-title">No detection events found for this journey.</p>
                </div>
              )}

              {/* ── View on Map button ──────────────────────────────────── */}
              <div className="vra-journey-actions">
                <button
                  className="vra-map-btn"
                  onClick={(e) => { e.stopPropagation(); setShowMap(true); }}
                  disabled={detections.length === 0}
                >
                  <Map size={13} />
                  View on Map
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Journey Map Popup ────────────────────────────────────────────── */}
      {showMap && detections && (
        <JourneyMapPopup
          journey={journey}
          detections={detections}
          legs={legs}
          historyMap={historyMap}
          config={config}
          networkId={networkId}
          onClose={() => setShowMap(false)}
        />
      )}
    </>
  );
};

/* ─── RTT Benchmark helpers ──────────────────────────────────────────────── */

/** Default benchmark RTT in seconds (5 minutes). */
export const DEFAULT_RTT_BENCHMARK_SECONDS = 300;

/** localStorage key for persisting the user's benchmark value. */
const RTT_BENCHMARK_STORAGE_KEY = 'vra_rtt_benchmark';

/** Load the saved benchmark from localStorage, falling back to the default. */
const loadBenchmark = () => {
  try {
    const raw = localStorage.getItem(RTT_BENCHMARK_STORAGE_KEY);
    if (raw !== null) {
      const parsed = Number(raw);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_RTT_BENCHMARK_SECONDS;
};

/** Persist the benchmark to localStorage. */
const saveBenchmark = (seconds) => {
  try { localStorage.setItem(RTT_BENCHMARK_STORAGE_KEY, String(seconds)); } catch { /* ignore */ }
};

/* ─── PlateGroup ─────────────────────────────────────────────────────────── */

/**
 * Classify a single journey as anomalous using the entry→exit pair delta.
 * Returns the delta % (or null if no history).
 */
const journeyDelta = (j, historyMap) => {
  if (j.rtt_seconds === null) return null;
  const key  = `${j.entry_camera_id}|${j.exit_camera_id}`;
  const hist = historyMap[key]?.avg ?? null;
  return calcDelta(j.rtt_seconds, hist);
};

/**
 * Classify a journey as anomalous.
 * Primary signal: RTT exceeds the user-configurable benchmark threshold.
 * Falls back to the historical-delta check when no benchmark is provided.
 *
 * @param {object} j            – journey row
 * @param {object} historyMap   – "fromId|toId" → { avg, min, max }
 * @param {number} [benchmarkSeconds]  – user-set RTT threshold (seconds)
 */
const isJourneyAnomalous = (j, historyMap, benchmarkSeconds) => {
  if (benchmarkSeconds != null && j.rtt_seconds != null) {
    return j.rtt_seconds > benchmarkSeconds;
  }
  // Fallback: historical delta > 40 %
  const d = journeyDelta(j, historyMap);
  return d !== null && d > 40;
};

/**
 * Worst anomaly delta across all journeys for a plate (used for sorting).
 * Returns -Infinity when no history is available for any journey.
 */
const plateWorstDelta = (journeys, historyMap) => {
  let worst = -Infinity;
  for (const j of journeys) {
    const d = journeyDelta(j, historyMap);
    if (d !== null && d > worst) worst = d;
  }
  return worst;
};

/**
 * Collapsible group for a single plate within a day panel.
 * Shows summary row; expands to list all journeys for that plate.
 * Anomalous journeys are listed first and expanded by default;
 * normal journeys are folded under a "show N normal" toggle.
 */
const PlateGroup = ({ plate, journeys, historyMap, historyLoading, config, networkId, benchmarkSeconds }) => {
  /* ── plate-level stats ─────────────────────────────────────────────────── */
  const totalRtt = journeys.reduce((s, j) => s + (j.rtt_seconds ?? 0), 0);
  const statusCounts = journeys.reduce((acc, j) => {
    acc[j.status] = (acc[j.status] || 0) + 1;
    return acc;
  }, {});

  /* ── classify journeys ─────────────────────────────────────────────────── */
  const anomalousJourneys = useMemo(
    () => journeys.filter(j => isJourneyAnomalous(j, historyMap, benchmarkSeconds))
                  .sort((a, b) => (b.rtt_seconds ?? 0) - (a.rtt_seconds ?? 0)),
    [journeys, historyMap, benchmarkSeconds],
  );
  const normalJourneys = useMemo(
    () => journeys.filter(j => !isJourneyAnomalous(j, historyMap, benchmarkSeconds)),
    [journeys, historyMap, benchmarkSeconds],
  );

  const hasAnomalyHint = anomalousJourneys.length > 0;

  /* ── expansion state — always starts collapsed until user clicks ───────── */
  const [isExpanded, setIsExpanded]         = useState(false);
  // If there are no anomalies, normal journeys are shown directly (no fold).
  // If there are anomalies, normal journeys start folded so anomalies stand out.
  const [showNormal, setShowNormal]         = useState(!hasAnomalyHint);

  // When history loads and anomalies are newly detected, fold normal journeys
  // so anomalies stand out — but do NOT auto-expand the plate.
  const prevHasAnomaly = useRef(hasAnomalyHint);
  useEffect(() => {
    if (!prevHasAnomaly.current && hasAnomalyHint) {
      setShowNormal(false);
    }
    prevHasAnomaly.current = hasAnomalyHint;
  }, [hasAnomalyHint]);

  /* ── visit index helpers (across all journeys sorted by start time) ─────── */
  const sortedAll = useMemo(
    () => [...journeys].sort((a, b) => parseLocalTs(a.journey_start) - parseLocalTs(b.journey_start)),
    [journeys],
  );
  const visitIndexMap = useMemo(() => {
    const m = {};
    sortedAll.forEach((j, i) => { m[j.id] = i + 1; });
    return m;
  }, [sortedAll]);

  return (
    <div className={`vra-plate-group ${hasAnomalyHint ? 'vra-plate-group--anomalous' : ''}`}>
      {/* ── Plate summary row ─────────────────────────────────────────────── */}
      <div
        className={`vra-plate-row ${isExpanded ? 'vra-plate-row--expanded' : ''} ${hasAnomalyHint ? 'vra-plate-row--anomalous' : ''}`}
        onClick={() => setIsExpanded(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setIsExpanded(v => !v); }}
        aria-expanded={isExpanded}
      >
        <div className="vra-plate-row-left">
          <Car size={14} className={`vra-plate-icon ${hasAnomalyHint ? 'vra-plate-icon--anomalous' : ''}`} />
          <span className="vra-plate-number">{plate}</span>
          {hasAnomalyHint && !historyLoading && (
            <span className="vra-anomaly-count-badge">
              <AlertTriangle size={9} />
              {anomalousJourneys.length} anomal{anomalousJourneys.length !== 1 ? 'ies' : 'y'}
            </span>
          )}
        </div>

        <div className="vra-plate-row-meta">
          <span className="vra-plate-meta-item">
            <span className="vra-plate-meta-label">Journeys</span>
            <span className="vra-plate-meta-value">{journeys.length}</span>
          </span>
          <span className="vra-plate-meta-item">
            <span className="vra-plate-meta-label">Total time</span>
            <span className="vra-plate-meta-value">{fmtDuration(totalRtt)}</span>
          </span>
          <span className="vra-plate-meta-item vra-plate-status-breakdown">
            {statusCounts.active     > 0 && <StatusPill status="active" />}
            {statusCounts.incomplete > 0 && <StatusPill status="incomplete" />}
          </span>
        </div>

        <div className="vra-plate-row-chevron">
          {isExpanded ? <ChevronUp size={13} className="ct-chevron" /> : <ChevronDown size={13} className="ct-chevron" />}
        </div>
      </div>

      {/* ── Journey list ──────────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="vra-journey-list">

          {/* ── Anomalous journeys (always shown, sorted worst-first) ──────── */}
          {anomalousJourneys.map((j) => (
            <JourneyRow
              key={j.id}
              journey={j}
              historyMap={historyMap}
              historyLoading={historyLoading}
              config={config}
              networkId={networkId}
              visitIndex={visitIndexMap[j.id]}
              visitTotal={journeys.length}
              benchmarkSeconds={benchmarkSeconds}
            />
          ))}

          {/* ── Normal journeys fold ───────────────────────────────────────── */}
          {normalJourneys.length > 0 && (
            <>
              {showNormal
                ? normalJourneys.map((j) => (
                    <JourneyRow
                      key={j.id}
                      journey={j}
                      historyMap={historyMap}
                      historyLoading={historyLoading}
                      config={config}
                      networkId={networkId}
                      visitIndex={visitIndexMap[j.id]}
                      visitTotal={journeys.length}
                      benchmarkSeconds={benchmarkSeconds}
                    />
                  ))

                : (
                  <button
                    className="vra-normal-fold-btn"
                    onClick={e => { e.stopPropagation(); setShowNormal(true); }}
                  >
                    <CheckCircle size={11} />
                    {normalJourneys.length} normal journey{normalJourneys.length !== 1 ? 's' : ''} — click to expand
                    <ChevronDown size={11} />
                  </button>
                )
              }
              {showNormal && (
                <button
                  className="vra-normal-fold-btn vra-normal-fold-btn--collapse"
                  onClick={e => { e.stopPropagation(); setIsExpanded(false); }}
                >
                  <ChevronUp size={11} />
                  Collapse
                </button>
              )}
            </>
          )}

          {/* ── Empty state (no journeys at all) ──────────────────────────── */}
          {journeys.length === 0 && (
            <div className="ct-empty" style={{ padding: '16px 44px' }}>
              <p className="ct-empty-title">No journeys for this plate.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── AnomalousJourneysTable ─────────────────────────────────────────────── */

/**
 * Flat table of all anomalous journeys (not grouped by plate).
 * Shown when the user clicks the "Anomalous Journeys" stat card.
 *
 * Props:
 *   journeys         {Array}   – all journeys for the selected day
 *   historyMap       {object}  – "fromId|toId" → { avg, min, max }
 *   benchmarkSeconds {number}  – user-set RTT threshold
 *   cameras          {Array}   – raw camera list [{ camera_id, camera_name, … }]
 *   config           {object}  – camera map config (cameras with pos_x/pos_y)
 *   onClose          {func}    – callback to close the panel
 */
const AnomalousJourneysTable = ({ journeys, historyMap, benchmarkSeconds, cameras = [], config, onClose }) => {
  const anomalous = useMemo(() => {
    return journeys
      .filter(j => isJourneyAnomalous(j, historyMap, benchmarkSeconds))
      .sort((a, b) => (b.rtt_seconds ?? 0) - (a.rtt_seconds ?? 0));
  }, [journeys, historyMap, benchmarkSeconds]);

  /**
   * Build a camera_id → camera_name lookup.
   * Prefer the raw cameras list (has camera_name); fall back to config.cameras
   * (which also stores camera_name from the setup modal save).
   */
  const cameraNameMap = useMemo(() => {
    const m = {};
    // config.cameras entries: { camera_id, camera_name, pos_x, pos_y }
    (config?.cameras || []).forEach(c => { if (c.camera_name) m[c.camera_id] = c.camera_name; });
    // raw cameras list overrides (more authoritative)
    cameras.forEach(c => { if (c.camera_name) m[c.camera_id] = c.camera_name; });
    return m;
  }, [cameras, config]);

  const camName = (id) => cameraNameMap[id] || id || '—';

  return (
    <div className="vra-anomaly-table-panel">
      {/* Panel header */}
      <div className="vra-anomaly-table-header">
        <span className="vra-anomaly-table-title">
          <AlertTriangle size={14} className="vra-anomaly-table-title-icon" />
          Anomalous Journeys
          <span className="vra-anomaly-table-count">{anomalous.length}</span>
        </span>
        <button
          className="ct-trm-modal-close-btn vra-anomaly-table-close"
          onClick={onClose}
          title="Close anomalous journeys view"
          aria-label="Close anomalous journeys view"
        >
          <X size={14} />
        </button>
      </div>

      {anomalous.length === 0 ? (
        <div className="ct-empty" style={{ padding: '24px' }}>
          <span className="ct-empty-icon">✅</span>
          <p className="ct-empty-title">No anomalous journeys</p>
          <p className="ct-empty-text">
            All journeys are within the benchmark RTT of {fmtDuration(benchmarkSeconds)}.
          </p>
        </div>
      ) : (
        <div className="vra-anomaly-table-scroll">
          {/* Table header */}
          <div className="vra-anomaly-tbl-head">
            <span>Plate</span>
            <span>Start</span>
            <span>End</span>
            <span>From</span>
            <span></span>
            <span>To</span>
            <span className="vra-anomaly-col-right">RTT</span>
            <span className="vra-anomaly-col-right">Benchmark</span>
            <span className="vra-anomaly-col-right">Over by</span>
            <span className="vra-anomaly-col-right">Status</span>
          </div>

          {anomalous.map((j) => {
            const overBy = j.rtt_seconds != null && benchmarkSeconds != null
              ? j.rtt_seconds - benchmarkSeconds
              : null;
            const overPct = overBy != null && benchmarkSeconds > 0
              ? ((overBy / benchmarkSeconds) * 100).toFixed(1)
              : null;

            return (
              <div key={j.id} className="vra-anomaly-tbl-row">
                <span className="vra-anomaly-plate">{j.plate}</span>
                <span className="vra-anomaly-time">{fmtDateTime(j.journey_start)}</span>
                <span className="vra-anomaly-time">{j.journey_end ? fmtDateTime(j.journey_end) : <em className="vra-in-progress">In progress</em>}</span>
                <span className="vra-anomaly-cam">{camName(j.entry_camera_id)}</span>
                <span className="vra-anomaly-arrow"><ArrowRight size={10} /></span>
                <span className="vra-anomaly-cam">{camName(j.exit_camera_id)}</span>
                <span className="vra-anomaly-col-right vra-anomaly-rtt">{fmtDuration(j.rtt_seconds)}</span>
                <span className="vra-anomaly-col-right vra-anomaly-benchmark">{fmtDuration(benchmarkSeconds)}</span>
                <span className="vra-anomaly-col-right vra-anomaly-over">
                  {overBy != null ? (
                    <span className="vra-anomaly-over-badge">
                      +{fmtDuration(overBy)}
                      {overPct !== null && <span className="vra-anomaly-over-pct"> ({overPct}%)</span>}
                    </span>
                  ) : '—'}
                </span>
                <span className="vra-anomaly-col-right">
                  <span className="vra-anomaly-indicator">
                    <AlertTriangle size={10} />
                    Anomalous
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── DayPanel ───────────────────────────────────────────────────────────── */

/**
 * Collapsible panel for a single calendar day.
 */
const PLATES_PAGE_SIZE = 10;

const DayPanel = ({ dateStr, journeys, historyMap, historyLoading, config, networkId, benchmarkSeconds }) => {
  const [isExpanded, setIsExpanded] = useState(true); // open by default
  const [platesPage, setPlatesPage] = useState(1);

  /* ── group journeys by plate, sort: most-anomalous first ──────────────── */
  const byPlate = useMemo(() => {
    const map = {};
    journeys.forEach(j => {
      if (!map[j.plate]) map[j.plate] = [];
      map[j.plate].push(j);
    });
    return Object.entries(map).sort(([, aJourneys], [, bJourneys]) => {
      // Sort plates with anomalous journeys first, then by worst RTT desc.
      const aHasAnomaly = aJourneys.some(j => isJourneyAnomalous(j, historyMap, benchmarkSeconds));
      const bHasAnomaly = bJourneys.some(j => isJourneyAnomalous(j, historyMap, benchmarkSeconds));
      if (bHasAnomaly !== aHasAnomaly) return bHasAnomaly ? 1 : -1;
      const aWorst = plateWorstDelta(aJourneys, historyMap);
      const bWorst = plateWorstDelta(bJourneys, historyMap);
      return bWorst - aWorst;
    });
  }, [journeys, historyMap, benchmarkSeconds]);

  const totalPlates   = byPlate.length;
  const totalJourneys = journeys.length;
  const anomalyCount  = useMemo(
    () => journeys.filter(j => isJourneyAnomalous(j, historyMap, benchmarkSeconds)).length,
    [journeys, historyMap, benchmarkSeconds],
  );

  const platesTotalPages = Math.max(1, Math.ceil(totalPlates / PLATES_PAGE_SIZE));
  const platesPageSafe   = Math.min(platesPage, platesTotalPages);
  const byPlatePage      = byPlate.slice(
    (platesPageSafe - 1) * PLATES_PAGE_SIZE,
    platesPageSafe * PLATES_PAGE_SIZE,
  );

  return (
    <div className="vra-day-panel">
      {/* ── Day header ──────────────────────────────────────────────────── */}
      <div
        className={`vra-day-header ${isExpanded ? 'vra-day-header--expanded' : ''}`}
        onClick={() => setIsExpanded(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setIsExpanded(v => !v); }}
        aria-expanded={isExpanded}
      >
        <div className="vra-day-header-left">
          <Calendar size={14} className="vra-day-icon" />
          <span className="vra-day-label">{fmtDay(dateStr)}</span>
        </div>
        <div className="vra-day-header-meta">
          <span className="vra-day-badge">{totalPlates} plate{totalPlates !== 1 ? 's' : ''}</span>
          <span className="vra-day-badge">{totalJourneys} journey{totalJourneys !== 1 ? 's' : ''}</span>
          {anomalyCount > 0 && !historyLoading && (
            <span className="vra-day-badge vra-day-badge--anomaly">
              <AlertTriangle size={10} />
              {anomalyCount} anomal{anomalyCount !== 1 ? 'ies' : 'y'}
            </span>
          )}
        </div>
        <div className="vra-day-chevron">
          {isExpanded ? <ChevronUp size={13} className="ct-chevron" /> : <ChevronDown size={13} className="ct-chevron" />}
        </div>
      </div>

      {/* ── Plate list ──────────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="vra-day-body">
          {byPlatePage.map(([plate, plateJourneys]) => (
            <PlateGroup
              key={plate}
              plate={plate}
              journeys={plateJourneys}
              historyMap={historyMap}
              historyLoading={historyLoading}
              config={config}
              networkId={networkId}
              benchmarkSeconds={benchmarkSeconds}
            />
          ))}

          {platesTotalPages > 1 && (
            <div className="ct-pagination vra-plates-pagination">
              <button
                className="ct-page-btn"
                onClick={() => setPlatesPage(1)}
                disabled={platesPageSafe === 1}
              >«</button>
              <button
                className="ct-page-btn"
                onClick={() => setPlatesPage(p => Math.max(1, p - 1))}
                disabled={platesPageSafe === 1}
              >‹</button>
              <span className="ct-page-info">
                Page {platesPageSafe} of {platesTotalPages}
                <span className="ct-page-count">
                  &nbsp;({totalPlates} plates)
                </span>
              </span>
              <button
                className="ct-page-btn"
                onClick={() => setPlatesPage(p => Math.min(platesTotalPages, p + 1))}
                disabled={platesPageSafe === platesTotalPages}
              >›</button>
              <button
                className="ct-page-btn"
                onClick={() => setPlatesPage(platesTotalPages)}
                disabled={platesPageSafe === platesTotalPages}
              >»</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── main component ─────────────────────────────────────────────────────── */

/**
 * VehicleRouteAnalysis
 *
 * Props:
 *   networkId        {string}  - selected network ID
 *   networkName      {string}  - display name
 *   networkTimezone  {string}  - IANA timezone for the network (e.g. 'Australia/Sydney')
 *   config           {object}  - camera map config with positions
 *   cameras          {Array}   - raw camera list
 *   selectedDay      {string}  - YYYY-MM-DD — shared with CameraTransitions
 *   onDayChange      {func}    - called with new YYYY-MM-DD when user navigates
 *   onConfigureMap   {func}    - optional; opens the camera setup modal
 *   onShowHelp       {func}    - optional; opens the LPR Journey feature intro
 */
const VehicleRouteAnalysis = ({ networkId, networkName, networkTimezone, config, cameras = [], selectedDay, onDayChange, onConfigureMap, onShowHelp }) => {
  const [journeys, setJourneys]             = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);

  const [historyMap, setHistoryMap]         = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError]     = useState(null);

  const [filterPlate, setFilterPlate]       = useState('');

  /* ── RTT benchmark ──────────────────────────────────────────────────────── */
  const [benchmarkSeconds, setBenchmarkSeconds] = useState(loadBenchmark);
  // Local input state (minutes string) for the benchmark input field
  const [benchmarkInput, setBenchmarkInput]     = useState(() => String(Math.round(loadBenchmark() / 60)));
  const [showBenchmarkEdit, setShowBenchmarkEdit] = useState(false);

  // Persist whenever the committed value changes
  useEffect(() => { saveBenchmark(benchmarkSeconds); }, [benchmarkSeconds]);

  const commitBenchmark = useCallback(() => {
    const mins = parseFloat(benchmarkInput);
    if (!Number.isNaN(mins) && mins > 0) {
      setBenchmarkSeconds(Math.round(mins * 60));
    } else {
      // Reset input to current value on bad input
      setBenchmarkInput(String(Math.round(benchmarkSeconds / 60)));
    }
    setShowBenchmarkEdit(false);
  }, [benchmarkInput, benchmarkSeconds]);

  /* ── Anomalous journeys panel ───────────────────────────────────────────── */
  const [showAnomalousTable, setShowAnomalousTable] = useState(false);

  /* ── fetch journeys ─────────────────────────────────────────────────────── */
  const fetchJourneys = useCallback(async () => {
    if (!networkId || !selectedDay) return;
    setJourneys([]);
    setIsLoading(true);
    setError(null);
    try {
      const data = await lprJourneyAPI.getVehicleRoutes(networkId, selectedDay, selectedDay);
      setJourneys(data);
    } catch (err) {
      setError(err.message || 'Failed to load journey data');
    } finally {
      setIsLoading(false);
    }
  }, [networkId, selectedDay]);

  useEffect(() => { fetchJourneys(); }, [fetchJourneys]);

  /* ── fetch historical baseline (always anchored to today, not selectedDay) ─ */
  const fetchHistory = useCallback(async () => {
    if (!networkId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await lprJourneyAPI.getHistoricalTransitions(networkId, HISTORY_WINDOW_DAYS);
      const map = {};
      data.forEach(r => {
        map[`${r.from_camera_id}|${r.to_camera_id}`] = {
          avg: r.history_avg_duration_seconds,
          min: r.history_min_duration_seconds ?? null,
          max: r.history_max_duration_seconds ?? null,
        };
      });
      setHistoryMap(map);
    } catch (err) {
      setHistoryError(err.message || 'Failed to load historical data');
    } finally {
      setHistoryLoading(false);
    }
  }, [networkId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  /* ── derived: filter to selected date only, then filter by plate ────────── */
  const filteredJourneys = useMemo(() => {
    // Only show journeys whose journey_start falls on the currently selected date.
    // The API already queries startDate=selectedDay&endDate=selectedDay, but we
    // guard here too in case stale data from a previous fetch is still in state.
    let result = journeys.filter(j => {
      const day = j.journey_start ? j.journey_start.slice(0, 10) : null;
      return day === selectedDay;
    });
    if (filterPlate) {
      const q = filterPlate.toLowerCase();
      result = result.filter(j => j.plate.toLowerCase().includes(q));
    }
    return result;
  }, [journeys, filterPlate, selectedDay]);

  const byDay = useMemo(() => {
    const map = {};
    filteredJourneys.forEach(j => {
      const day = j.journey_start ? j.journey_start.slice(0, 10) : selectedDay;
      if (!map[day]) map[day] = [];
      map[day].push(j);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a)); // newest first
  }, [filteredJourneys, selectedDay]);

  /* ── summary stats ──────────────────────────────────────────────────────── */
  const totalPlates   = useMemo(() => new Set(filteredJourneys.map(j => j.plate)).size, [filteredJourneys]);
  const totalJourneys = filteredJourneys.length;
  const avgRtt        = useMemo(() => {
    const withRtt = filteredJourneys.filter(j => j.rtt_seconds !== null);
    if (!withRtt.length) return null;
    return withRtt.reduce((s, j) => s + j.rtt_seconds, 0) / withRtt.length;
  }, [filteredJourneys]);

  const anomalyJourneyCount = useMemo(() => {
    return filteredJourneys.filter(j => isJourneyAnomalous(j, historyMap, benchmarkSeconds)).length;
  }, [filteredJourneys, historyMap, benchmarkSeconds]);

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="ct-container">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="ct-header">
        <div className="ct-header-left">
          <h3 className="ct-title">
            <Route size={16} className="ct-title-icon" />
            Vehicle Route Analysis
            {networkName && <span className="ct-title-network">— {networkName}</span>}
          </h3>
          <p className="ct-subtitle">
            Per-plate journey routes, timing, and anomaly detection
          </p>
        </div>
        <button
          className="ct-refresh-btn"
          onClick={() => { fetchJourneys(); fetchHistory(); }}
          disabled={isLoading}
          title="Refresh"
        >
          <RefreshCw size={14} className={isLoading ? 'ct-spin' : ''} />
        </button>
      </div>

      {/* ── Note: date navigator is shared — rendered by CameraTransitions ── */}
      <div className="vra-shared-date-note">
        <Calendar size={12} className="vra-shared-date-icon" />
        <span>Showing data for <strong>{fmtDay(selectedDay)}</strong> — use the date navigator above to change the day.</span>
      </div>

      {/* ── Loading / Error ─────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="ct-loading">
          <div className="ct-spinner" />
          <span>Loading journey data…</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="ct-error">
          <span>⚠️ {error}</span>
          <button className="ct-retry-btn" onClick={fetchJourneys}>Retry</button>
        </div>
      )}

      {!isLoading && !error && journeys.length === 0 && (
        <LprJourneyGhostPreview
          imageSrc={lprJourneyVehicleRouteAnalysisImg}
          title="What Vehicle Route Analysis looks like"
          description="Search by licence plate to see each vehicle's full journey across your cameras — route, timing, and whether it looks normal or slow. Configure your map and let traffic flow to see journey data."
          primaryLabel="Configure Map"
          onPrimaryClick={onConfigureMap}
          onHelpClick={onShowHelp}
        />
      )}

      {!isLoading && !error && journeys.length > 0 && (
        <>
          {/* ── Benchmark control bar ─────────────────────────────────────── */}
          <div className="vra-benchmark-bar">
            <span className="vra-benchmark-bar-label">
              <Settings size={12} className="vra-benchmark-bar-icon" />
              RTT Benchmark:
            </span>
            {showBenchmarkEdit ? (
              <span className="vra-benchmark-edit-group">
                <input
                  className="vra-benchmark-input"
                  type="number"
                  min="0.1"
                  step="0.5"
                  value={benchmarkInput}
                  onChange={e => setBenchmarkInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitBenchmark();
                    if (e.key === 'Escape') {
                      setBenchmarkInput(String(Math.round(benchmarkSeconds / 60)));
                      setShowBenchmarkEdit(false);
                    }
                  }}
                  autoFocus
                  aria-label="Benchmark RTT in minutes"
                />
                <span className="vra-benchmark-unit">min</span>
                <button className="vra-benchmark-save-btn" onClick={commitBenchmark}>Save</button>
                <button
                  className="vra-benchmark-cancel-btn"
                  onClick={() => {
                    setBenchmarkInput(String(Math.round(benchmarkSeconds / 60)));
                    setShowBenchmarkEdit(false);
                  }}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <span className="vra-benchmark-display">
                <span className="vra-benchmark-value">{fmtDuration(benchmarkSeconds)}</span>
                <button
                  className="vra-benchmark-edit-btn"
                  onClick={() => setShowBenchmarkEdit(true)}
                  title="Edit RTT benchmark"
                >
                  Edit
                </button>
              </span>
            )}
            <span className="vra-benchmark-bar-hint">
              Journeys with RTT &gt; benchmark are flagged as anomalous.
            </span>
          </div>

          {/* ── Summary stats ─────────────────────────────────────────────── */}
          <div className="ct-stats-row">
            <StatCard
              icon={Car}
              label="Unique Plates"
              value={totalPlates.toLocaleString()}
              sub={`on ${fmtDay(selectedDay)}`}
            />
            <StatCard
              icon={Route}
              label="Total Journeys"
              value={totalJourneys.toLocaleString()}
              sub="across all plates"
            />
            <StatCard
              icon={Clock}
              label="Avg Journey Time"
              value={fmtDuration(avgRtt)}
              sub="mean RTT across all journeys"
            />
            {/* ── Clickable anomalous journeys card ─────────────────────── */}
            <div
              className={`ct-stat-card vra-anomaly-stat-card ${anomalyJourneyCount > 0 ? 'vra-anomaly-stat-card--active' : ''} ${showAnomalousTable ? 'vra-anomaly-stat-card--selected' : ''} vra-anomaly-stat-card--clickable`}
              onClick={() => setShowAnomalousTable(v => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setShowAnomalousTable(v => !v); }}
              aria-expanded={showAnomalousTable}
              aria-label="Toggle anomalous journeys list"
              title="Click to view all anomalous journeys"
            >
              <div className="ct-stat-icon">
                <AlertTriangle size={16} />
              </div>
              <div className="ct-stat-body">
                <p className="ct-stat-value">{anomalyJourneyCount.toLocaleString()}</p>
                <p className="ct-stat-label">Anomalous Journeys</p>
                <p className="ct-stat-sub">
                  {anomalyJourneyCount > 0
                    ? <span className="vra-anomaly-stat-click-hint"><List size={10} /> {showAnomalousTable ? 'hide list' : 'click to view list'}</span>
                    : 'all within benchmark'}
                </p>
              </div>
            </div>
          </div>

          {/* ── Anomalous journeys flat table (toggled by card click) ─────── */}
          {showAnomalousTable && (
            <AnomalousJourneysTable
              journeys={filteredJourneys}
              historyMap={historyMap}
              benchmarkSeconds={benchmarkSeconds}
              cameras={cameras}
              config={config}
              onClose={() => setShowAnomalousTable(false)}
            />
          )}

          {/* ── History error banner ───────────────────────────────────────── */}
          {historyError && (
            <div className="ct-history-error">
              ⚠️ Historical baseline unavailable: {historyError}
            </div>
          )}

          {/* ── Plate filter ──────────────────────────────────────────────── */}
          <div className="ct-table-section vra-filter-bar">
            <div className="ct-table-header">
              <span className="ct-chart-title">
                Journeys by Plate
                <span className="ct-table-count">{filteredJourneys.length}</span>
              </span>
              <div className="ct-filters">
                <input
                  className="ct-filter-input"
                  placeholder="Filter by plate…"
                  value={filterPlate}
                  onChange={e => setFilterPlate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Day panels ────────────────────────────────────────────────── */}
          <div className="vra-day-panels">
            {byDay.length === 0 && (
              <div className="ct-table-empty">No plates match the current filter.</div>
            )}
            {byDay.map(([dateStr, dayJourneys]) => (
              <DayPanel
                key={dateStr}
                dateStr={dateStr}
                journeys={dayJourneys}
                historyMap={historyMap}
                historyLoading={historyLoading}
                config={config}
                networkId={networkId}
                benchmarkSeconds={benchmarkSeconds}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleRouteAnalysis;
